import { Brand, BrandIntelligence, CompetitiveIntel, CreativeSystem, MarketIntelligence, SocialAndDigital } from "@/schema/brand-intelligence";
import { AudienceIntelligence } from "@/schema/brand-intelligence";

const brand: Brand = {
  name: "MyWonder Technologies Private Limited",
  industry: ["Educational Software", "EdTech"],
  sub_industry: ["AI-powered educational devices for children", "Screen-free learning companions", "Smart toys with conversational AI for ages 3-10"],
  founded_year: 2024,
  founded_original_name: "MyWonder Technologies Private Limited",
  headquarters: ["Bangalore, India"],
  tagline_primary: "Make play smarter and learning effortless, ensuring each moment is filled with wonder.",
  mission: '"To make play smarter and learning effortless through imaginative, tactile play."',
  vision: '"For every child to have a Wonder at home—talking, asking, and imagining without screens."',
  overview:
    "MyWonder is a Bengaluru startup offering Wonder, a screen-free, AI-powered device for kids aged 3–10. It combines voice-based stories, quizzes, and games with tactile play for imaginative, screen-free learning.",
  scale: {
    revenue: 0,
    employees_estimate: 5,
    markets_served_count: 1,
    operating_countries_count: 1,
  },
  global_presence: {
    regions: [
      {
        region: "India",
        headquarters: "Bangalore, India",
        active_markets: ["India"],
        market_type: "Direct (D2C via website, with plans for Amazon and Flipkart)",
      },
    ],
    key_markets_by_revenue_share: [
      {
        region: "India",
        share_percent: 100,
        notes:
          "Primary and sole launch market as of 2024-2025. The company has set ambitious targets of reaching 100,000 units in its first year and achieving 1% market penetration within 18 months, translating to USD 4.5 million in projected revenues.",
      },
    ],
  },
};

const creativeSystem: CreativeSystem = {
  visual_identity: {
    colors: {
      primary: [
        {
          name: "MyWonder Orange",
          hex: "#FF6B35",
        },
        {
          name: "MyWonder Deep Blue",
          hex: "#1E3A8A",
        },
        {
          name: "MyWonder Teal/Turquoise",
          hex: "#14B8A6",
        },
      ],
      secondary: [
        {
          name: "Soft Yellow",
          hex: "#FCD34D",
        },
        {
          name: "Light Purple/Lavender",
          hex: "#A78BFA",
        },
        {
          name: "Warm Coral",
          hex: "#FB923C",
        },
      ],
      accent: [
        {
          name: "Action Green",
          hex: "#10B981",
        },
        {
          name: "White",
          hex: "#FFFFFF",
        },
        {
          name: "Charcoal Gray",
          hex: "#374151",
        },
      ],
    },
    typography: [
      {
        font_family: "Sans-serif",
        weight: "Bold (700), Semi-Bold (600)",
        usage: "Headlines, hero text, product names, CTAs",
      },
      {
        font_family: "Sans-serif",
        weight: "Regular (400), Medium (500)",
        usage: "Body copy, descriptions, feature lists",
      },
      {
        font_family: "System UI font stack",
        weight: "Medium (500), Semi-Bold (600)",
        usage: "Buttons, labels, navigation",
      },
    ],
    logos: [
      {
        variant: "Primary",
        url: "https://mywonder.in/cdn/shop/files/AA_Full_Logo.png?v=1760077948",
      },
    ],
  },
  verbal_identity: {
    voice_traits: ["Empathetic", "Wonder-filled", "Reassuring", "Playful yet Educational", "Innovative", "Child-centric", "Screen-free advocate"],
    taglines: [
      "Make play smarter and learning effortless, ensuring each moment is filled with wonder.",
      "Safe, Age-appropriate & Independent Learning through Play",
      "Learn From Safe & Intelligent AI Companions",
      "Speak - Listen - Learn",
      "Designed for children's curiosity and parents' peace of mind",
    ],
    messaging_dos: [],
    messaging_donts: [],
    banned_words: ["screen time", "digital entertainment", "passive consumption", "addictive", "babysitter device"],
  },
  creative_assets: [
    "https://assets.zocket.com/uploads/0a495b2a-366f-4f62-a9c9-ac8d0f613bea-Screenshot_2026-02-11_at_4.40.11_PM.png",
    "https://assets.zocket.com/uploads/e260c7ec-e739-403a-89bd-205a092e929f-Screenshot_2026-02-11_at_4.43.53_PM.png",
  ],
  compliance: {
    brand_safety_guidelines: [],
    copyright_trademark_usage: [],
  },
  brand_architecture: {
    available_brands: ["MyWonder"],
    selected_brand: "MyWonder",
  },
  ai_creative_memory: {
    design_memory: {
      pattern_accuracy: "94%",
      patterns_learned: 342,
      avg_generation: "1.5s",
    },
    style_drift_alerts: [
      {
        title: "MyWonder Logo",
        description: "Logo placement inconsistent with brand guidelines",
        severity: "medium",
      },
    ],
    preference_learning: [
      { preference: "Screen-Free", score: 92, change: 5 },
      { preference: "Educational", score: 88, change: 4 },
      { preference: "Playful", score: 86, change: 3 },
      { preference: "AI-Powered", score: 82, change: 6 },
    ],
  },
};

const marketIntelligence: MarketIntelligence = {
  kpis: {
    sentiment: 7.5,
    sentiment_graph: [
      {
        metric: "Beta User Engagement",
        value: "81 min/day",
        delta: "New baseline",
      },
      {
        metric: "Active User Rate",
        value: "60%",
        delta: "3x/week",
      },
      {
        metric: "Market Penetration Goal",
        value: "1%",
        delta: "+1%",
      },
      {
        metric: "Unit Sales Target (Year 1)",
        value: "100K units",
        delta: "New target",
      },
      {
        metric: "Revenue Projection (Year 1)",
        value: "$4.5M USD",
        delta: "New projection",
      },
      {
        metric: "Investor Confidence",
        value: "8/10",
        delta: "+8",
      },
      {
        metric: "Content Partnership Strength",
        value: "90%",
        delta: "From publishers",
      },
      {
        metric: "Product-Market Fit Indicator",
        value: "7/10",
        delta: "On beta traction",
      },
    ],
  },
  sentiment_summary: {
    time_range: ["October 2024", "February 2026"],
    themes: {
      positive: [
        "Screen-Free Innovation Leadership",
        "Strong Beta Engagement Metrics",
        "Adaptive Learning Technology",
        "Premium Content Partnerships",
        "Investor and Accelerator Validation",
        "Parental Control & Safety Focus",
        "Founder Credibility",
      ],
      neutral: ["Market Entry Timing", "Pricing Strategy", "Distribution Strategy Evolution", "Technology Maturity"],
      negative: ["AI Toy Category Concerns", "Limited Public Customer Reviews", "Competitive Pressure", "Regulatory Uncertainty", "Dependency Risk"],
    },
  },
  distribution: [
    {
      brand: "Miko (India)",
      share: 35,
    },
    {
      brand: "PlayShifu (India)",
      share: 20,
    },
    {
      brand: "Smartivity (India)",
      share: 15,
    },
    {
      brand: "Funskool/Hasbro/Mattel (Combined)",
      share: 12,
    },
    {
      brand: "Others",
      share: 18,
    },
  ],
  sentiment_by_product: [
    {
      product: "Wonder Core Device",
      sentiment: 7.5,
      change_delta: 0,
    },
    {
      product: "Interactive Character Cards",
      sentiment: 8,
      change_delta: 0,
    },
    {
      product: "Adaptive Learning AI",
      sentiment: 8.5,
      change_delta: 0,
    },
    {
      product: "Parent Companion App",
      sentiment: 7,
      change_delta: 0,
    },
    {
      product: "Audio Content Library",
      sentiment: 8,
      change_delta: 0,
    },
    {
      product: "Dual-Microphone System",
      sentiment: 7.5,
      change_delta: 0,
    },
    {
      product: "Multilingual Support",
      sentiment: 6.5,
      change_delta: 0,
    },
    {
      product: "Subscription Content Packs",
      sentiment: 0,
      change_delta: 0,
    },
  ],
  top_topics: {
    praise: [
      {
        topic: "Screen-Free Design",
        weight: 10,
      },
      {
        topic: "High Engagement Levels",
        weight: 10,
      },
      {
        topic: "Adaptive Learning System",
        weight: 9,
      },
      {
        topic: "Quality Content Partnerships",
        weight: 9,
      },
      {
        topic: "Safety & Age-Appropriateness",
        weight: 8,
      },
      {
        topic: "Multi-Character Variety",
        weight: 8,
      },
      {
        topic: "Voice Interface Innovation",
        weight: 8,
      },
    ],
    complaints: [
      {
        topic: "Limited Customer Review Data",
        weight: 10,
      },
      {
        topic: "AI Toy Category Privacy Concerns",
        weight: 10,
      },
      {
        topic: "Pricing Accessibility",
        weight: 9,
      },
      {
        topic: "Distribution Limitations",
        weight: 8,
      },
      {
        topic: "Content Breadth Concerns",
        weight: 8,
      },
      {
        topic: "Multilingual Rollout Delay",
        weight: 8,
      },
      {
        topic: "Unproven Scale Performance",
        weight: 8,
      },
    ],
  },
};

const competitiveIntel: CompetitiveIntel = {
  primary_competitors: [
    {
      name: "Miko Technologies",
      threat_level: "high",
      market_share: 9,
      sentiment: 72,
    },
    {
      name: "Yoto",
      threat_level: "medium-high",
      market_share: 2.5,
      sentiment: 85,
    },
    {
      name: "Tonies SE",
      threat_level: "medium",
      market_share: 0.5,
      sentiment: 88,
    },
    {
      name: "PlayShifu Technologies",
      threat_level: "medium",
      market_share: 6,
      sentiment: 78,
    },
    {
      name: "Bondu",
      threat_level: "low-medium",
      market_share: 0.5,
      sentiment: 75,
    },
    {
      name: "Storypod",
      threat_level: "low",
      market_share: 0.5,
      sentiment: 80,
    },
  ],
  secondary_competitors: [
    {
      name: "Smartivity Labs",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Amazon",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Google",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Funskool India / Hamleys",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "LeapFrog Enterprises",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "VTech Holdings",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
  ],
  share_of_voice: [
    {
      channel: "Social Media",
      distribution: {
        Miko: 35,
        PlayShifu: 25,
        Yoto: 15,
        Tonies: 10,
        MyWonder: 42,
        Others: 10,
      },
    },
    {
      channel: "Search/Digital",
      distribution: {
        Miko: 30,
        PlayShifu: 25,
        Yoto: 10,
        Tonies: 10,
        MyWonder: 12,
        Others: 15,
      },
    },
    {
      channel: "Retail/Distribution",
      distribution: {
        Miko: 20,
        PlayShifu: 15,
        Yoto: 10,
        Tonies: 10,
        MyWonder: 34,
        Others: 13,
      },
    },
  ],
  competitive_benchmarking: [
    {
      metric_name: "Brand Awareness",
      brands: [
        {
          brand_name: "Miko",
          score: 65,
        },
        {
          brand_name: "PlayShifu",
          score: 58,
        },
        {
          brand_name: "Smartivity",
          score: 45,
        },
        {
          brand_name: "Yoto",
          score: 25,
        },
        {
          brand_name: "Tonies",
          score: 20,
        },
        {
          brand_name: "MyWonder",
          score: 53,
        },
      ],
    },
    {
      metric_name: "Customer Satisfaction Score",
      brands: [
        {
          brand_name: "Yoto",
          score: 85,
        },
        {
          brand_name: "Tonies",
          score: 82,
        },
        {
          brand_name: "Miko",
          score: 72,
        },
        {
          brand_name: "PlayShifu",
          score: 78,
        },
        {
          brand_name: "Smartivity",
          score: 75,
        },
        {
          brand_name: "MyWonder",
          score: 0,
        },
      ],
    },
    {
      metric_name: "Product Innovation Score",
      brands: [
        {
          brand_name: "MyWonder",
          score: 90,
        },
        {
          brand_name: "Miko",
          score: 85,
        },
        {
          brand_name: "Yoto",
          score: 75,
        },
        {
          brand_name: "PlayShifu",
          score: 80,
        },
        {
          brand_name: "Tonies",
          score: 70,
        },
        {
          brand_name: "Smartivity",
          score: 65,
        },
      ],
    },
    {
      metric_name: "Market Penetration India",
      brands: [
        {
          brand_name: "Miko",
          score: 55,
        },
        {
          brand_name: "PlayShifu",
          score: 45,
        },
        {
          brand_name: "Smartivity",
          score: 35,
        },
        {
          brand_name: "Funskool (traditional)",
          score: 70,
        },
        {
          brand_name: "Yoto",
          score: 10,
        },
        {
          brand_name: "Tonies",
          score: 5,
        },
        {
          brand_name: "MyWonder",
          score: 2,
        },
      ],
    },
    {
      metric_name: "Price Competitiveness Score",
      brands: [
        {
          brand_name: "MyWonder",
          score: 85,
        },
        {
          brand_name: "Smartivity",
          score: 80,
        },
        {
          brand_name: "PlayShifu",
          score: 70,
        },
        {
          brand_name: "Miko",
          score: 45,
        },
        {
          brand_name: "Yoto",
          score: 40,
        },
        {
          brand_name: "Tonies",
          score: 35,
        },
      ],
    },
    {
      metric_name: "Content Library Depth",
      brands: [
        {
          brand_name: "Tonies",
          score: 95,
        },
        {
          brand_name: "Yoto",
          score: 90,
        },
        {
          brand_name: "Miko",
          score: 80,
        },
        {
          brand_name: "PlayShifu",
          score: 65,
        },
        {
          brand_name: "MyWonder",
          score: 50,
        },
        {
          brand_name: "Smartivity",
          score: 40,
        },
      ],
    },
  ],
  positioning: {
    competitive_advantages: ["Screen-free + AI", "Value Pricing", "Made for India", "Adaptive Learning", "Parental Control"],
    improvement_areas: ["Content Velocity", "Distribution", "Brand Building", "Social Proof", "Funding"],
    disruption_watchlist: ["Giggle Nest", "Toycra", "EMoMee", "Imagimake", "Butterfly Edufields"],
  },
};

const audienceIntelligence: AudienceIntelligence = {
  audience_summary:
    "My Wonder's core audience consists of millennial and Gen-Z parents (ages 25-34, representing 52-58% of buyers) who are tech-savvy, highly educated (68-75% with bachelor's degrees or higher), and predominantly urban (62-70% in Tier-1 cities). These are dual-income households with annual per capita income crossing ₹1.97 lakh, with 38% of urban households having dual incomes. Education is foremost on Indian parents' minds, and they look out for toys which provide educational value through play. They are concerned about excessive screen time impacting attention span, creativity, and social skills, making them ideal candidates for My Wonder's screen-free AI solution. There is a notable shift towards premium and branded toys, with parents more inclined to invest in premium, educational, and technologically advanced toys. The segment has evolved from offline, price-sensitive shopping to one powered by digital discovery, personalised shopping, and premium offerings. These digitally native parents are three times more likely than earlier generations to conduct online research before purchasing, with internet reviews (39.4%) and other parents (20.2%) being their most frequent sources of information. The purchase journey spans 23-48 days from awareness to purchase, with heavy reliance on mobile devices (58-65%) and social media channels, particularly Instagram (32-38%) and YouTube (25-30%) for product discovery and validation.",
  core_demographics: {
    primary_age_range: "25-34",
    gender_split: {
      female: 65,
      male: 35,
    },
    demographics_distribution: {
      age: [
        { age_range: "12-18", percentage: 41 },
        { age_range: "25-34", percentage: 38 },
        { age_range: "35-44", percentage: 21 },
      ],
      education: [
        { education_level: "School", percentage: 50 },
        { education_level: "Bachelor's Degree", percentage: 68 },
        { education_level: "Master's Degree", percentage: 32 },
      ],
      geographic: [
        { location: "Urban", percentage: 62 },
        { location: "Suburban", percentage: 38 },
        { location: "Rural", percentage: 0 },
      ],
    },
    income_median_usd: 3900,
    location_type: "Urban",
  },
  occupation_distribution: [
    { occupation: "Working Professionals", percentage: 50 },
    { occupation: "Students", percentage: 30 },
    { occupation: "Homemakers", percentage: 20 },
  ],
  interests: {
    top_categories: [
      {
        category: "Early Childhood Education",
        score: 9.5,
      },
      {
        category: "Child Development & Parenting",
        score: 9,
      },
      {
        category: "Educational Technology (EdTech)",
        score: 8.5,
      },
      {
        category: "STEM Learning",
        score: 8.5,
      },
      {
        category: "Screen-Free Activities",
        score: 8,
      },
      {
        category: "Sustainable/Eco-Friendly Products",
        score: 7.5,
      },
      {
        category: "Smart Home & AI Technology",
        score: 7,
      },
      {
        category: "Health & Wellness (Child-focused)",
        score: 8,
      },
      {
        category: "Digital Parenting Communities",
        score: 7.5,
      },
      {
        category: "Premium Children's Products",
        score: 8,
      },
    ],
    brand_affinities: [
      {
        brand: "BYJU's",
        affinity: 45,
      },
      {
        brand: "Smartivity",
        affinity: 42,
      },
      {
        brand: "Miko",
        affinity: 38,
      },
      {
        brand: "FirstCry",
        affinity: 55,
      },
      {
        brand: "Mamaearth",
        affinity: 48,
      },
      {
        brand: "PlayShifu",
        affinity: 35,
      },
      {
        brand: "LEGO",
        affinity: 52,
      },
      {
        brand: "Amazon India",
        affinity: 68,
      },
      {
        brand: "Flipkart",
        affinity: 62,
      },
      {
        brand: "Cuemath",
        affinity: 32,
      },
    ],
  },
  behavioral_insights: {
    shopping_behavior: {
      purchase_channel_distribution: {
        "Online (D2C website)": 43.7,
        "E-commerce marketplaces (Amazon, Flipkart)": 36.4,
        "Social media/Instagram shopping": 9.7,
        "Offline specialty toy stores": 6.3,
        "Word-of-mouth/referral": 3.9,
      },
      purchase_frequency: "1.2-1.5x per year",
      avg_order_value_usd: 67.5,
    },
    decision_journey: {
      stages: [
        {
          stage: 1,
          name: "Discovery",
          description: "Parent identifies screen-time concerns, seeks educational alternatives through social media, parenting forums, or peer recommendations",
          duration_days: 10,
        },
        {
          stage: 2,
          name: "Evaluation",
          description: "Reviews online testimonials, compares with Miko/other AI toys, watches demo videos, reads expert reviews, joins parenting groups",
          duration_days: 15,
        },
        {
          stage: 3,
          name: "Social Proof",
          description: "Seeks recommendations from parent networks, checks Instagram/YouTube reviews, reads Amazon/website reviews, consults spouse/family",
          duration_days: 7,
        },
        {
          stage: 4,
          name: "Conversion",
          description: "Finalizes purchase on D2C website or marketplace, often triggered by promotional offer, Shark Tank mention, or festival season",
          duration_days: 2,
        },
        {
          stage: 5,
          name: "Engagement & Loyalty",
          description: "Monitors child's usage via companion app, purchases additional content cards, shares experience in parent communities, becomes brand advocate",
          duration_days: 60,
        },
      ],
    },
    media_and_device_usage: {
      device_preference: {
        "Mobile (Smartphone)": 59.7,
        "Desktop/Laptop": 26.7,
        Tablet: 9.7,
        "Smart TV (for research/reviews)": 3.9,
      },
      media_channels: {
        Instagram: 30,
        "YouTube (reviews, unboxing)": 23.6,
        "WhatsApp (parent groups)": 17.2,
        "Facebook (parenting communities)": 12,
        "Google Search": 8.6,
        "LinkedIn (professional networks)": 3.4,
        "Parenting blogs/websites": 5.2,
      },
    },
  },
  platforms: {
    distribution: {
      "Online (D2C website)": 43.7,
      "E-commerce marketplaces (Amazon, Flipkart)": 36.4,
      "Social media/Instagram shopping": 9.7,
      "Offline specialty toy stores": 6.3,
      "Word-of-mouth/referral": 3.9,
    },
  },
};

const socialAndDigital: SocialAndDigital = {
  platform_metrics: [
    {
      platform: "Instagram",
      followers: 168,
      health_score: 50,
    },
    {
      platform: "LinkedIn",
      followers: 329,
      health_score: 45,
    },
    {
      platform: "Website",
      followers: 329,
      health_score: 80,
    },
    {
      platform: "Mobile App",
      followers: 0,
      health_score: 80,
    },
    {
      platform: "YouTube",
      followers: 123,
      health_score: 0,
    },
    {
      platform: "Facebook",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "TikTok",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "Twitter/X",
      followers: 0,
      health_score: 0,
    },
  ],
  sentiment_distribution: [
    {
      label: "Positive",
      percentage: 45,
    },
    {
      label: "Neutral",
      percentage: 34,
    },
    {
      label: "Negative",
      percentage: 22,
    },
  ],
  recent_comments: [
    {
      user: "tech_enthusiast",
      platform: "Instagram",
      timeAgo: "1 day ago",
      status: "positive",
      commentText: "Beautiful product photography!",
      postContext: "Product photography",
    },
    {
      user: "Jane Smith",
      platform: "Instagram",
      timeAgo: "2 days ago",
      status: "neutral",
      commentText: "Interesting approach to AI education!",
      postContext: "Teaching content",
    },
  ],
  engagement: {
    engagement_by_platform: [
      {
        platform: "Instagram",
        engagement_rate: 20,
      },
      {
        platform: "LinkedIn",
        engagement_rate: 5,
      },
      {
        platform: "Facebook",
        engagement_rate: 0,
      },
      {
        platform: "TikTok",
        engagement_rate: 0,
      },
      {
        platform: "YouTube",
        engagement_rate: 30,
      },
    ],
    best_performing_content_types: [
      {
        content_type: "Product Demo Videos / Reels",
        performance: 9,
      },
      {
        content_type: "Founder Story / Mission-Driven Content",
        performance: 8,
      },
      {
        content_type: "Product Launch Announcements",
        performance: 7,
      },
      {
        content_type: "Educational Content / Parenting Tips",
        performance: 6,
      },
      {
        content_type: "User-Generated Content / Testimonials",
        performance: 5,
      },
      {
        content_type: "Behind-the-Scenes / Product Development",
        performance: 4,
      },
      {
        content_type: "Partnership Announcements",
        performance: 7,
      },
    ],
    follower_growth_trend: [
      {
        month: "October",
        followers: 49,
      },
      {
        month: "December",
        followers: 57,
      },
      {
        month: "January",
        followers: 75,
      },
      {
        month: "February",
        followers: 148,
      },
    ],
  },
};

export const mBrandIntelligenceMyWonder: BrandIntelligence = {
  brand,
  creative_system: creativeSystem,
  market_intelligence: marketIntelligence,
  competitive_intel: competitiveIntel,
  audience_intelligence: audienceIntelligence,
  social_and_digital: socialAndDigital,
};
